import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { IFeedPostItem } from "@eagerdog/interfaces";

import Button from "src/components/Button/Button";
import Icon from "src/components/Icon/Icon";

import styles from "./ClubActivityCard.module.scss";

interface IProps {
    post: IFeedPostItem;
    link: string;
}

const initials = (name: string): string =>
    name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join("") || "?";

// Club summary inside a club_followed post: cover photo with the logo
// overlapping it, then name, location and blurb with a "View club" action.
// Live club details come from the feed read; the post's snapshot is the
// fallback if the club has since been removed.
const ClubActivityCard: React.FC<IProps> = ({ post, link }) => {
    const navigate = useNavigate();
    // On the permalink page the card is subject context for a club update:
    // `post.club` is hydrated but there is no follow activity.
    const a: Partial<NonNullable<IFeedPostItem["activity"]>> = post.activity || {};
    const club = post.club;
    if (!post.activity && !club) return null;

    const name = club?.club_name || a.target_name || "Club";
    const logo = club?.logo_url || a.target_image_url;
    const cover = club?.cover_image_url;
    const place = [club?.city ?? a.city, club?.state ?? a.state, club?.country].filter(Boolean).join(", ");

    return (
        <div className={styles.ClubActivityCard}>
            <Link to={link} className={`${styles.cover} ${cover ? "" : styles.coverFallback}`} aria-label={name}>
                {cover && <img src={cover} alt="" />}
            </Link>
            <div className={styles.body}>
                <Link to={link} className={styles.logo}>
                    {logo ? <img src={logo} alt={name} /> : <span>{initials(name)}</span>}
                </Link>
                <div className={styles.text}>
                    <span className={styles.kicker}>Kennel club</span>
                    <Link to={link} className={styles.name}>{name}</Link>
                    {place && (
                        <span className={styles.place}>
                            <Icon type="location" className={styles.placeIcon} />
                            {place}
                        </span>
                    )}
                </div>
                <Button variant="secondary" className={styles.viewButton} onClick={() => navigate(link)}>
                    View club
                    <Icon type="caretRight" className={styles.viewCaret} />
                </Button>
            </div>
        </div>
    );
};

export default ClubActivityCard;
